/**
 * Admin Errors tab.
 *
 * Lives at /admin-errors, admin-only. Auto-triages every un-classified row
 * on entry (so info/noise vanish immediately) and marks the last-seen
 * timestamp so the sidebar badge clears.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  Copy,
  RefreshCcw,
  ShieldCheck,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const SEVERITY_STYLES = {
  critical: "bg-rose-100 text-rose-800 border-rose-200",
  warning: "bg-amber-100 text-amber-800 border-amber-200",
  info: "bg-sky-100 text-sky-800 border-sky-200",
  noise: "bg-slate-100 text-slate-700 border-slate-200",
};

// Source pills for the top of the tab. `All` is the null filter; the rest
// map 1:1 to the `source` string stored on each error row. Labels stay short
// so the pills wrap cleanly on narrow screens.
const SOURCE_PILLS = [
  { value: "__all__", label: "All" },
  { value: "network_error", label: "network" },
  { value: "route_optimization", label: "route_opt" },
  { value: "maps_auth", label: "maps_key" },
  { value: "react_render_crash", label: "react_render" },
  { value: "unhandled_error", label: "js_error" },
  { value: "unhandled_rejection", label: "unhandled_rejection" },
  { value: "backend_exception", label: "backend_exception" },
  { value: "apify_actor_failure", label: "apify_actor" },
  { value: "firecrawl_failure", label: "firecrawl" },
  { value: "photo_check_failure", label: "photo_check" },
  { value: "google_api", label: "google_api" },
  { value: "atlas_blip", label: "atlas_blip" },
  { value: "client_error", label: "client_error" },
];

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * Serialise a single error row into a clipboard-friendly text block.
 * Matches the "Copy" behaviour on the reference app — one row per
 * property, code-fenced context and stack for easy paste-into-issue.
 */
function serializeError(row, index = null) {
  const cls = row.classification || {};
  const dev = row.device_info || {};
  const idPrefix = index !== null ? `#${index + 1} ` : "";
  const lines = [
    `${idPrefix}${cls.friendly_title || row.source}`,
    `severity : ${cls.severity || "warning"}`,
    `source   : ${row.source}`,
    `time     : ${fmtDateTime(row.created_at)}`,
  ];
  if (row.occurrences && row.occurrences > 1) {
    lines.push(`seen     : ${row.occurrences}×`);
    if (row.first_seen_at) lines.push(`first    : ${fmtDateTime(row.first_seen_at)}`);
    if (row.last_seen_at) lines.push(`last     : ${fmtDateTime(row.last_seen_at)}`);
  }
  if (row.ip) lines.push(`ip       : ${row.ip}`);
  if (row.message) lines.push(`message  : ${row.message}`);
  if (cls.explanation) lines.push(`why      : ${cls.explanation}`);
  const devLine = [dev.browser, dev.os, dev.device, dev.pwa ? "PWA" : null, dev.screen]
    .filter(Boolean)
    .join(" · ");
  if (devLine) lines.push(`device   : ${devLine}`);
  if (dev.url) lines.push(`url      : ${dev.url}`);
  if (row.context && Object.keys(row.context).length) {
    lines.push("context  :");
    lines.push("```");
    lines.push(JSON.stringify(row.context, null, 2));
    lines.push("```");
  }
  if (row.stack) {
    lines.push("stack    :");
    lines.push("```");
    lines.push(row.stack);
    lines.push("```");
  }
  return lines.join("\n");
}

async function writeClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    // Fallback for older browsers / http contexts
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch {
    return "";
  }
}

export default function ErrorsTab() {
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("active");
  const [source, setSource] = useState("__all__");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [triaging, setTriaging] = useState(false);
  // Full unfiltered set for the current status, so source pills can show
  // real per-source counts. `rows` (below) applies the source filter to
  // this list.
  const [allRows, setAllRows] = useState([]);

  const fetchErrors = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch every row for the current status (up to 500), then filter by
      // source client-side. This lets the pills show meaningful counts and
      // switching between sources is instant with no network round-trip.
      const params = new URLSearchParams();
      params.set("status", status);
      params.set("limit", "500");
      const { data } = await api.get(`/admin/app-errors?${params}`);
      setAllRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Couldn't load errors", { description: formatApiError(e) });
    } finally {
      setLoading(false);
    }
  }, [status]);

  // Apply the source pill filter client-side.
  const rows = useMemo(() => {
    if (source === "__all__") return allRows;
    return allRows.filter((r) => r.source === source);
  }, [allRows, source]);

  // Also derive setRows to keep in-place updates (mark resolved, etc.)
  const setRows = useCallback((updater) => {
    setAllRows((prev) => (typeof updater === "function" ? updater(prev) : updater));
  }, []);

  // On mount: mark seen (clears sidebar badge) + auto-triage + fetch.
  useEffect(() => {
    (async () => {
      try {
        await api.post("/admin/app-errors/mark-seen", {});
        await api.post("/admin/app-errors/auto-triage", {});
      } catch {
        /* silent — mount-time housekeeping */
      }
      fetchErrors();
    })();
  }, [fetchErrors]);

  const runAutoTriage = async () => {
    setTriaging(true);
    try {
      const { data } = await api.post("/admin/app-errors/auto-triage", {});
      toast.success(
        `Auto-triaged: ${data.auto_archived} row${data.auto_archived === 1 ? "" : "s"} archived`,
      );
      fetchErrors();
    } catch (e) {
      toast.error("Auto-triage failed", { description: formatApiError(e) });
    } finally {
      setTriaging(false);
    }
  };

  const archiveAllSafe = async () => {
    try {
      const { data } = await api.post("/admin/app-errors/archive-all", {});
      toast.success(`Archived ${data.archived} safe-to-hide row${data.archived === 1 ? "" : "s"}`);
      fetchErrors();
    } catch (e) {
      toast.error("Bulk archive failed", { description: formatApiError(e) });
    }
  };

  const toggleResolved = async (row) => {
    try {
      const next = !row.resolved;
      await api.patch(`/admin/app-errors/${row.id}`, { resolved: next });
      // In-place update so scroll position holds
      setRows((rs) =>
        rs
          .map((r) => (r.id === row.id ? { ...r, resolved: next } : r))
          .filter((r) => (status === "active" ? !r.resolved : status === "resolved" ? r.resolved : true)),
      );
    } catch (e) {
      toast.error("Update failed", { description: formatApiError(e) });
    }
  };

  const copyOne = async (row) => {
    const text = serializeError(row);
    const ok = await writeClipboard(text);
    if (ok) toast.success("Copied error to clipboard");
    else toast.error("Copy failed — grab the details from the row expander");
  };

  const copyVisible = async () => {
    if (rows.length === 0) {
      toast.info("Nothing to copy");
      return;
    }
    const text = rows.map((r, i) => serializeError(r, i)).join("\n\n---\n\n");
    const ok = await writeClipboard(text);
    if (ok) toast.success(`Copied ${rows.length} error${rows.length === 1 ? "" : "s"} to clipboard`);
    else toast.error("Copy failed");
  };

  // Per-source counts on the current status tab so pills can show a
  // little number (matching the reference App Health UI).
  const sourceCounts = useMemo(() => {
    const acc = { __all__: rows.length };
    for (const r of rows) {
      acc[r.source] = (acc[r.source] || 0) + 1;
    }
    return acc;
  }, [rows]);

  const counts = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        const s = r.classification?.severity || "warning";
        acc[s] = (acc[s] || 0) + 1;
        return acc;
      },
      {},
    );
  }, [rows]);

  return (
    <div className="p-6 space-y-6" data-testid="errors-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-rose-600" /> Errors
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Client crashes, backend 5xx and integration failures — auto-classified into
            severity bands. Info/noise rows are safe to bulk-archive.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchErrors}
            disabled={loading}
            data-testid="errors-refresh"
          >
            <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={runAutoTriage}
            disabled={triaging}
            data-testid="errors-auto-triage"
          >
            <ShieldCheck className="h-4 w-4 mr-2" />
            {triaging ? "Triaging…" : "Auto-triage"}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={archiveAllSafe}
            className="bg-slate-800 hover:bg-slate-900 text-white"
            data-testid="errors-archive-safe"
          >
            <CircleCheck className="h-4 w-4 mr-2" />
            Archive All Safe-to-Hide
          </Button>
        </div>
      </div>

      {/* Status filter tabs — Active / Resolved / All */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
          {[
            { key: "active", label: "Active" },
            { key: "resolved", label: "Resolved" },
            { key: "all", label: "All" },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setStatus(t.key)}
              className={`px-3 py-1.5 text-sm ${
                status === t.key
                  ? "bg-slate-900 text-white"
                  : "bg-white text-slate-700 hover:bg-slate-50"
              }`}
              data-testid={`errors-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="text-xs text-slate-500 ml-auto flex items-center gap-3">
          {["critical", "warning", "info", "noise"].map((s) =>
            counts[s] ? (
              <span key={s} className={`px-2 py-0.5 rounded border ${SEVERITY_STYLES[s]}`}>
                {counts[s]} {s}
              </span>
            ) : null,
          )}
          <span className="text-slate-500">{total} total</span>
        </div>
      </div>

      {/* Source pills — only render pills that actually have data on the
          current status tab so the row doesn't get noisy. */}
      <div className="flex flex-wrap gap-2" data-testid="errors-source-pills">
        {SOURCE_PILLS.filter(
          (p) => p.value === "__all__" || sourceCounts[p.value],
        ).map((p) => {
          const active = source === p.value;
          const n = sourceCounts[p.value] || 0;
          return (
            <button
              key={p.value}
              type="button"
              onClick={() => setSource(p.value)}
              className={[
                "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                active
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50",
              ].join(" ")}
              data-testid={`errors-pill-${p.value}`}
            >
              {p.label}
              {n > 0 ? (
                <span
                  className={`ml-1.5 text-[10px] ${
                    active ? "text-slate-300" : "text-slate-500"
                  }`}
                >
                  {n}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Copy-visible-page action row */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">
          {rows.length} {status} error{rows.length === 1 ? "" : "s"}
          {source !== "__all__" ? ` · filtered by ${source}` : ""}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={copyVisible}
          disabled={rows.length === 0}
          data-testid="errors-copy-visible"
        >
          <Copy className="h-4 w-4 mr-2" /> Copy visible page ({rows.length})
        </Button>
      </div>

      {/* List */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        {rows.length === 0 && !loading ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="errors-empty">
            <CircleCheck className="h-8 w-8 mx-auto mb-3 text-emerald-500" />
            No errors here. Nice quiet week.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {rows.map((r) => {
              const isOpen = !!expanded[r.id];
              const cls = r.classification || {};
              const sev = cls.severity || "warning";
              return (
                <li key={r.id} data-testid={`errors-row-${r.id}`}>
                  <div
                    className="px-4 py-3 flex items-start gap-3 cursor-pointer hover:bg-slate-50"
                    onClick={() => setExpanded((s) => ({ ...s, [r.id]: !isOpen }))}
                  >
                    <div className="mt-0.5">
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4 text-slate-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900">
                          {cls.friendly_title || r.source}
                        </span>
                        <Badge className={`text-[10px] uppercase border ${SEVERITY_STYLES[sev]}`}>
                          {sev}
                        </Badge>
                        <span className="text-xs text-slate-500 font-mono">{r.source}</span>
                        {r.occurrences && r.occurrences > 1 ? (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-slate-50 border-slate-300 text-slate-700 font-mono"
                            data-testid={`errors-occurrences-${r.id}`}
                            title={`Seen ${r.occurrences} times${r.first_seen_at ? " since " + fmtDateTime(r.first_seen_at) : ""}`}
                          >
                            ×{r.occurrences}
                          </Badge>
                        ) : null}
                      </div>
                      <div className="text-sm text-slate-700 truncate mt-0.5">
                        {r.message || cls.explanation}
                      </div>
                      <div className="text-xs text-slate-400 mt-1 flex items-center gap-3">
                        <span>
                          {r.occurrences && r.occurrences > 1 && r.last_seen_at
                            ? `last seen ${timeAgo(r.last_seen_at)}`
                            : timeAgo(r.created_at)}
                        </span>
                        {r.device_info?.browser ? (
                          <span>
                            {r.device_info.browser} · {r.device_info.os || "?"}
                            {r.device_info.pwa ? " · PWA" : ""}
                          </span>
                        ) : null}
                        {r.context?.online === false ? (
                          <span className="text-amber-600" title="User was offline when this fired">
                            offline
                          </span>
                        ) : null}
                        {r.resolved ? (
                          <span className="text-emerald-600">Resolved</span>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyOne(r);
                        }}
                        data-testid={`errors-copy-${r.id}`}
                        title="Copy this error to clipboard"
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleResolved(r);
                        }}
                        data-testid={`errors-toggle-${r.id}`}
                      >
                        {r.resolved ? "Reopen" : "Mark resolved"}
                      </Button>
                    </div>
                  </div>
                  {isOpen ? (
                    <div className="bg-slate-50 px-11 py-3 space-y-3">
                      {cls.explanation ? (
                        <div className="text-sm text-slate-700">{cls.explanation}</div>
                      ) : null}
                      {r.stack ? (
                        <details open>
                          <summary className="text-xs font-medium text-slate-500 cursor-pointer">
                            Stack trace
                          </summary>
                          <pre className="mt-2 text-xs bg-slate-900 text-slate-100 p-3 rounded overflow-x-auto max-h-64">
                            {r.stack}
                          </pre>
                        </details>
                      ) : null}
                      {r.context && Object.keys(r.context).length ? (
                        <details>
                          <summary className="text-xs font-medium text-slate-500 cursor-pointer">
                            Context
                          </summary>
                          <pre className="mt-2 text-xs bg-white border border-slate-200 p-3 rounded overflow-x-auto max-h-40">
                            {JSON.stringify(r.context, null, 2)}
                          </pre>
                        </details>
                      ) : null}
                      {r.device_info && Object.keys(r.device_info).length ? (
                        <details>
                          <summary className="text-xs font-medium text-slate-500 cursor-pointer">
                            Device info
                          </summary>
                          <pre className="mt-2 text-xs bg-white border border-slate-200 p-3 rounded overflow-x-auto">
                            {JSON.stringify(r.device_info, null, 2)}
                          </pre>
                        </details>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
